import type { FC } from 'react'
import type { GetShopItems_getShopItems } from '@g4g/graphql/src/shop/__generated__/GetShopItems'
import HeadlessSelect, { SelectItem } from '@g4g/ui/src/adopted/HeadlessSelect'
import Spinner from '@g4g/ui/src/internal/Spinner'
import RarityTag from '../../shared/RarityTag'
import { getProducts, toShoppingCartItem } from '../util/cart'
import { Rarity } from '@g4g/graphql/src/shop/__generated__/globalTypes'
import { useShoppingCartOpsDispatch } from '../ShoppingCartProvider'
import { useActiveRecipientShopItems } from '../../shared/hooks/useActiveRecipientShopItems'
import ShopItemLink from '../../shared/ShopItemLink'

const constructSelectItem = (
  id: number,
  rarity: Rarity,
  disabled: boolean,
  tagSize: number
): SelectItem => ({
  id,
  disabled,
  label: <RarityTag rarity={rarity} size={tagSize} />,
})

const SubscriptionLink: FC<{ cartProduct: GetShopItems_getShopItems }> = ({ cartProduct }) => (
  <ShopItemLink kebabOrShopItem={cartProduct} className="cursor-pointer">
    {cartProduct.subscription?.rarity ? (
      <RarityTag rarity={cartProduct.subscription.rarity} size={30} />
    ) : (
      <div>Edit</div>
    )}
  </ShopItemLink>
)

const RaritySelectable: FC<{
  cartProduct: GetShopItems_getShopItems
  subscription: GetShopItems_getShopItems[]
}> = ({ cartProduct, subscription }) => {
  const dispatch = useShoppingCartOpsDispatch()
  const selectedIndex = subscription.findIndex(
    (p) => p.lootRewardPackage.productId === cartProduct.lootRewardPackage.productId
  )

  return (
    <HeadlessSelect
      disabled={false}
      items={subscription.map((p, i) =>
        constructSelectItem(
          i,
          p.subscription?.rarity ?? Rarity.Rare,
          !!p.limit?.reachedAllTimeLimit,
          20
        )
      )}
      selectedItem={constructSelectItem(
        selectedIndex,
        cartProduct.subscription?.rarity ?? Rarity.Rare,
        false,
        25
      )}
      buttonClassName="w-full text-base h-full sm:text-base ring-white/20 sm:w-[215px]"
      dropdownBackgroundClassName="bg-[#181c25]"
      setSelectedItem={(item) =>
        dispatch({
          type: 'add-to-cart',
          items: [toShoppingCartItem({ product: subscription[item.id], totalQuantity: 1 })],
          replace: 'soft',
        })
      }
    />
  )
}

const RaritySelectSuccess: FC<{
  cartProduct: GetShopItems_getShopItems
  products: GetShopItems_getShopItems[]
}> = ({ cartProduct, products }) => {
  const subscription = getProducts(cartProduct.shopItemId, products)

  if (!subscription || subscription.length === 0) {
    return <SubscriptionLink cartProduct={cartProduct} />
  }

  if (subscription.length === 1) {
    return <RarityTag rarity={subscription[0].subscription?.rarity ?? Rarity.Rare} size={25} />
  }

  return <RaritySelectable subscription={subscription} cartProduct={cartProduct} />
}

const RaritySelect: FC<{ cartProduct: GetShopItems_getShopItems }> = ({ cartProduct }) => {
  // Need to query here to find all the other available
  // rarities of the current subscription
  const { data, error, loading } = useActiveRecipientShopItems()

  if (loading) {
    return <Spinner className="w-5 h-5" delay={500} />
  }

  if (error || !data || !data.getShopItems) {
    return <SubscriptionLink cartProduct={cartProduct} />
  }

  return <RaritySelectSuccess cartProduct={cartProduct} products={data.getShopItems} />
}

export default RaritySelect
